import { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [stats, setStats] = useState(null);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classDetails, setClassDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [editingClass, setEditingClass] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", school: "" });
  const [editError, setEditError] = useState("");
  const [deletingClass, setDeletingClass] = useState(null);

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      const [statsRes, classesRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard/stats`),
        axios.get(`${API}/admin/dashboard/classes`)
      ]);
      setStats(statsRes.data);
      setClasses(classesRes.data);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setLoading(false);
    }
  };

  // Fetch class details
  const fetchClassDetails = async (token) => {
    try {
      const res = await axios.get(`${API}/admin/dashboard/class/${token}`);
      setClassDetails(res.data);
      setSelectedClass(token);
    } catch (err) {
      console.error("Error fetching class details:", err);
    }
  };

  // Check authentication on mount
  useEffect(() => {
    const authExpiry = localStorage.getItem("admin_auth_expiry");
    if (authExpiry && Date.now() < parseInt(authExpiry)) {
      setIsAuthenticated(true);
    } else {
      setLoading(false);
    }
  }, []);

  // Auto-refresh every 30 seconds (only when authenticated)
  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboardData();
      const interval = setInterval(fetchDashboardData, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  // Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);

    try {
      const response = await axios.post(`${API}/admin/login`, {
        password: password
      });

      if (response.data.success) {
        setIsAuthenticated(true);
        // Store expiry timestamp
        const expiry = response.data.expiry * 1000; // Convert to milliseconds
        localStorage.setItem("admin_auth_expiry", expiry.toString());
        setPassword("");
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setLoginError("Invalid password");
      } else {
        setLoginError("Login failed. Please try again.");
      }
    } finally {
      setLoggingIn(false);
    }
  };

  // Logout handler
  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem("admin_auth_expiry");
    setStats(null);
    setClasses([]);
    setClassDetails(null);
  };

  // Edit class handler
  const handleEditClick = (cls) => {
    setEditingClass(cls);
    setEditForm({ name: cls.name, school: cls.school });
    setEditError("");
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditError("");

    try {
      await axios.put(`${API}/admin/classes/${editingClass.token}`, {
        name: editForm.name.trim(),
        school: editForm.school.trim()
      });

      // Refresh data
      await fetchDashboardData();
      setEditingClass(null);
    } catch (err) {
      if (err.response?.status === 409) {
        setEditError("A class with this name already exists");
      } else {
        setEditError("Failed to update class. Please try again.");
      }
    }
  };

  // Delete class handler
  const handleDeleteClick = (cls) => {
    setDeletingClass(cls);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(`${API}/admin/classes/${deletingClass.token}`);

      // Refresh data
      await fetchDashboardData();
      setDeletingClass(null);
    } catch (err) {
      alert("Failed to delete class. Please try again.");
    }
  };

  // Filter classes by search term
  const filteredClasses = classes.filter(cls =>
    cls.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cls.school.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cls.token.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getInviteLink = (token) => {
    return `${window.location.origin}/pulse/${token}`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <div style={{
        minHeight: "100vh",
        background: "#F9FAFB",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        fontFamily: "'Work Sans', sans-serif"
      }}>
        <div style={{
          background: "white",
          borderRadius: "1rem",
          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
          padding: "2rem",
          width: "100%",
          maxWidth: "400px"
        }}>
          <h1 style={{
            fontSize: "1.5rem",
            fontWeight: "600",
            color: "#111827",
            marginBottom: "0.5rem",
            textAlign: "center"
          }}>
            Admin Login
          </h1>
          <p style={{
            color: "#6B7280",
            fontSize: "0.875rem",
            marginBottom: "1.5rem",
            textAlign: "center"
          }}>
            Enter your password to access the dashboard
          </p>

          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Admin password"
              required
              disabled={loggingIn}
              style={{
                width: "100%",
                padding: "0.875rem",
                borderRadius: "0.75rem",
                border: "1px solid #E5E7EB",
                fontSize: "1rem",
                outline: "none",
                marginBottom: "1rem",
                boxSizing: "border-box"
              }}
            />

            {loginError && (
              <p style={{
                color: "#DC2626",
                fontSize: "0.875rem",
                marginBottom: "1rem",
                textAlign: "center"
              }}>
                {loginError}
              </p>
            )}

            <button
              type="submit"
              disabled={loggingIn || !password.trim()}
              style={{
                width: "100%",
                padding: "0.875rem",
                border: "none",
                borderRadius: "0.75rem",
                background: loggingIn || !password.trim() ? "#E5E7EB" : "#3B82F6",
                color: loggingIn || !password.trim() ? "#9CA3AF" : "white",
                fontSize: "1rem",
                fontWeight: "500",
                cursor: loggingIn || !password.trim() ? "not-allowed" : "pointer",
                fontFamily: "'Work Sans', sans-serif"
              }}
            >
              {loggingIn ? "Logging in..." : "Login"}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: "100vh",
      background: "#F9FAFB",
      padding: "2rem",
      fontFamily: "'Work Sans', sans-serif"
    }}>
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: "2rem", display: "flex", justifyContent: "space-between", alignItems: "start" }}>
          <div>
            <h1 style={{
              fontSize: "2rem",
              fontWeight: "600",
              color: "#111827",
              marginBottom: "0.5rem"
            }}>
              Admin Dashboard
            </h1>
            <p style={{ color: "#6B7280", fontSize: "0.875rem" }}>
              Monitor all classes and submissions
            </p>
          </div>
          <button
            onClick={handleLogout}
            style={{
              padding: "0.5rem 1rem",
              border: "1px solid #E5E7EB",
              borderRadius: "0.5rem",
              background: "white",
              color: "#374151",
              fontSize: "0.875rem",
              fontWeight: "500",
              cursor: "pointer",
              fontFamily: "'Work Sans', sans-serif"
            }}
          >
            Logout
          </button>
        </div>

        {/* Stats Overview */}
        {stats && (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "1rem",
            marginBottom: "2rem"
          }}>
            <StatsCard
              title="Total Classes"
              value={stats.total_classes}
              color="#3B82F6"
            />
            <StatsCard
              title="Active Today"
              value={stats.active_today}
              color="#10B981"
            />
            <StatsCard
              title="Submissions Today"
              value={stats.total_submissions_today}
              color="#F59E0B"
            />
          </div>
        )}

        {/* Search Bar */}
        <div style={{ marginBottom: "1.5rem" }}>
          <input
            type="text"
            placeholder="Search classes by name, school, or token..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "0.875rem 1rem",
              borderRadius: "0.75rem",
              border: "1px solid #E5E7EB",
              fontSize: "1rem",
              outline: "none",
              boxSizing: "border-box"
            }}
          />
        </div>

        {/* Classes List */}
        <div style={{
          background: "white",
          borderRadius: "1rem",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          overflow: "hidden"
        }}>
          <div style={{
            padding: "1rem 1.5rem",
            borderBottom: "1px solid #E5E7EB",
            background: "#F9FAFB"
          }}>
            <h2 style={{ fontSize: "1.125rem", fontWeight: "600", color: "#111827" }}>
              Classes ({filteredClasses.length})
            </h2>
          </div>

          {filteredClasses.length === 0 ? (
            <div style={{ padding: "2rem", textAlign: "center", color: "#6B7280" }}>
              No classes found
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#F9FAFB", borderBottom: "1px solid #E5E7EB" }}>
                    <th style={tableHeaderStyle}>Class Name</th>
                    <th style={tableHeaderStyle}>School</th>
                    <th style={tableHeaderStyle}>Token</th>
                    <th style={tableHeaderStyle}>Submissions Today</th>
                    <th style={tableHeaderStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClasses.map((cls, index) => (
                    <tr
                      key={cls.id}
                      style={{
                        borderBottom: index < filteredClasses.length - 1 ? "1px solid #E5E7EB" : "none"
                      }}
                    >
                      <td style={tableCellStyle}>{cls.name}</td>
                      <td style={tableCellStyle}>{cls.school}</td>
                      <td style={tableCellStyle}>
                        <code style={{
                          background: "#F3F4F6",
                          padding: "0.25rem 0.5rem",
                          borderRadius: "0.25rem",
                          fontSize: "0.875rem"
                        }}>
                          {cls.token}
                        </code>
                      </td>
                      <td style={tableCellStyle}>
                        <span style={{
                          background: cls.submission_count_today > 0 ? "#D1FAE5" : "#F3F4F6",
                          color: cls.submission_count_today > 0 ? "#065F46" : "#6B7280",
                          padding: "0.25rem 0.75rem",
                          borderRadius: "9999px",
                          fontSize: "0.875rem",
                          fontWeight: "500"
                        }}>
                          {cls.submission_count_today}
                        </span>
                      </td>
                      <td style={tableCellStyle}>
                        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                          <button
                            onClick={() => fetchClassDetails(cls.token)}
                            style={{
                              ...actionButtonStyle,
                              background: "#3B82F6",
                              color: "white"
                            }}
                          >
                            View
                          </button>
                          <button
                            onClick={() => handleEditClick(cls)}
                            style={{
                              ...actionButtonStyle,
                              background: "#F59E0B",
                              color: "white"
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteClick(cls)}
                            style={{
                              ...actionButtonStyle,
                              background: "#EF4444",
                              color: "white"
                            }}
                          >
                            Delete
                          </button>
                          <button
                            onClick={() => copyToClipboard(getInviteLink(cls.token))}
                            style={{
                              ...actionButtonStyle,
                              background: "#F3F4F6",
                              color: "#374151"
                            }}
                          >
                            Copy Link
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Class Details Modal */}
        {classDetails && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
            zIndex: 1000
          }}>
            <div style={{
              background: "white",
              borderRadius: "1rem",
              maxWidth: "600px",
              width: "100%",
              maxHeight: "80vh",
              overflow: "auto",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)"
            }}>
              <div style={{
                padding: "1.5rem",
                borderBottom: "1px solid #E5E7EB",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}>
                <h2 style={{ fontSize: "1.25rem", fontWeight: "600", color: "#111827" }}>
                  {classDetails.class_info.name}
                </h2>
                <button
                  onClick={() => setClassDetails(null)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "1.5rem",
                    cursor: "pointer",
                    color: "#6B7280"
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ padding: "1.5rem" }}>
                <div style={{ marginBottom: "1.5rem" }}>
                  <h3 style={{ fontSize: "0.875rem", fontWeight: "600", color: "#6B7280", marginBottom: "0.5rem" }}>
                    Class Information
                  </h3>
                  <DetailRow label="School" value={classDetails.class_info.school} />
                  <DetailRow label="Token" value={classDetails.class_info.token} />
                  <DetailRow label="Dismissal Time" value={classDetails.class_info.dismissal_time} />
                </div>

                <div style={{ marginBottom: "1.5rem" }}>
                  <h3 style={{ fontSize: "0.875rem", fontWeight: "600", color: "#6B7280", marginBottom: "0.5rem" }}>
                    Today's Activity
                  </h3>
                  <DetailRow label="Total Submissions" value={classDetails.today_submissions} />

                  {classDetails.today_aggregate?.counts && (
                    <>
                      <DetailRow label="Dismissed" value={classDetails.today_aggregate.counts.dismissed || 0} />
                      <DetailRow label="On Bus" value={classDetails.today_aggregate.counts.bus || 0} />
                      <DetailRow label="Reached Home" value={classDetails.today_aggregate.counts.home || 0} />
                      <DetailRow label="Delayed" value={classDetails.today_aggregate.counts.delayed || 0} />
                    </>
                  )}
                </div>

                <div>
                  <h3 style={{ fontSize: "0.875rem", fontWeight: "600", color: "#6B7280", marginBottom: "0.5rem" }}>
                    Recent Submissions ({classDetails.submission_list.length})
                  </h3>
                  <div style={{
                    maxHeight: "200px",
                    overflow: "auto",
                    background: "#F9FAFB",
                    borderRadius: "0.5rem",
                    padding: "0.5rem"
                  }}>
                    {classDetails.submission_list.length === 0 ? (
                      <p style={{ padding: "0.5rem", color: "#6B7280", fontSize: "0.875rem" }}>
                        No submissions yet
                      </p>
                    ) : (
                      classDetails.submission_list.map((sub, idx) => (
                        <div key={idx} style={{
                          padding: "0.5rem",
                          fontSize: "0.875rem",
                          color: "#374151"
                        }}>
                          Device: {sub.device_id_hash} • Date: {sub.date}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Class Modal */}
        {editingClass && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
            zIndex: 1000
          }}>
            <div style={{
              background: "white",
              borderRadius: "1rem",
              maxWidth: "500px",
              width: "100%",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)"
            }}>
              <div style={{
                padding: "1.5rem",
                borderBottom: "1px solid #E5E7EB",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}>
                <h2 style={{ fontSize: "1.25rem", fontWeight: "600", color: "#111827" }}>
                  Edit Class
                </h2>
                <button
                  onClick={() => setEditingClass(null)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "1.5rem",
                    cursor: "pointer",
                    color: "#6B7280"
                  }}
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleEditSubmit} style={{ padding: "1.5rem" }}>
                <div style={{ marginBottom: "1rem" }}>
                  <label style={{
                    display: "block",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    color: "#374151",
                    marginBottom: "0.5rem"
                  }}>
                    Class Name
                  </label>
                  <input
                    type="text"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    required
                    style={{
                      width: "100%",
                      padding: "0.75rem",
                      borderRadius: "0.5rem",
                      border: "1px solid #E5E7EB",
                      fontSize: "1rem",
                      outline: "none",
                      boxSizing: "border-box"
                    }}
                  />
                </div>

                <div style={{ marginBottom: "1rem" }}>
                  <label style={{
                    display: "block",
                    fontSize: "0.875rem",
                    fontWeight: "500",
                    color: "#374151",
                    marginBottom: "0.5rem"
                  }}>
                    School
                  </label>
                  <input
                    type="text"
                    value={editForm.school}
                    onChange={(e) => setEditForm({ ...editForm, school: e.target.value })}
                    required
                    style={{
                      width: "100%",
                      padding: "0.75rem",
                      borderRadius: "0.5rem",
                      border: "1px solid #E5E7EB",
                      fontSize: "1rem",
                      outline: "none",
                      boxSizing: "border-box"
                    }}
                  />
                </div>

                {editError && (
                  <p style={{
                    color: "#DC2626",
                    fontSize: "0.875rem",
                    marginBottom: "1rem"
                  }}>
                    {editError}
                  </p>
                )}

                <div style={{ display: "flex", gap: "0.75rem" }}>
                  <button
                    type="button"
                    onClick={() => setEditingClass(null)}
                    style={{
                      flex: 1,
                      padding: "0.75rem",
                      border: "1px solid #E5E7EB",
                      borderRadius: "0.5rem",
                      background: "white",
                      color: "#374151",
                      fontSize: "1rem",
                      fontWeight: "500",
                      cursor: "pointer"
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      flex: 1,
                      padding: "0.75rem",
                      border: "none",
                      borderRadius: "0.5rem",
                      background: "#3B82F6",
                      color: "white",
                      fontSize: "1rem",
                      fontWeight: "500",
                      cursor: "pointer"
                    }}
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deletingClass && (
          <div style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
            zIndex: 1000
          }}>
            <div style={{
              background: "white",
              borderRadius: "1rem",
              maxWidth: "400px",
              width: "100%",
              boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)",
              padding: "1.5rem"
            }}>
              <h2 style={{
                fontSize: "1.25rem",
                fontWeight: "600",
                color: "#111827",
                marginBottom: "0.75rem"
              }}>
                Delete Class?
              </h2>
              <p style={{
                color: "#6B7280",
                fontSize: "0.875rem",
                marginBottom: "1.5rem"
              }}>
                Are you sure you want to delete "{deletingClass.name}"? This will permanently delete all associated data including submissions and aggregates. This action cannot be undone.
              </p>

              <div style={{ display: "flex", gap: "0.75rem" }}>
                <button
                  onClick={() => setDeletingClass(null)}
                  style={{
                    flex: 1,
                    padding: "0.75rem",
                    border: "1px solid #E5E7EB",
                    borderRadius: "0.5rem",
                    background: "white",
                    color: "#374151",
                    fontSize: "1rem",
                    fontWeight: "500",
                    cursor: "pointer"
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  style={{
                    flex: 1,
                    padding: "0.75rem",
                    border: "none",
                    borderRadius: "0.5rem",
                    background: "#EF4444",
                    color: "white",
                    fontSize: "1rem",
                    fontWeight: "500",
                    cursor: "pointer"
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Helper Components
function StatsCard({ title, value, color }) {
  return (
    <div style={{
      background: "white",
      padding: "1.5rem",
      borderRadius: "1rem",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
    }}>
      <p style={{
        fontSize: "0.875rem",
        color: "#6B7280",
        marginBottom: "0.5rem"
      }}>
        {title}
      </p>
      <p style={{
        fontSize: "2rem",
        fontWeight: "600",
        color: color
      }}>
        {value}
      </p>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={{
      display: "flex",
      justifyContent: "space-between",
      padding: "0.5rem 0",
      fontSize: "0.875rem"
    }}>
      <span style={{ color: "#6B7280" }}>{label}:</span>
      <span style={{ fontWeight: "500", color: "#111827" }}>{value}</span>
    </div>
  );
}

// Styles
const tableHeaderStyle = {
  padding: "0.75rem 1rem",
  textAlign: "left",
  fontSize: "0.875rem",
  fontWeight: "600",
  color: "#374151"
};

const tableCellStyle = {
  padding: "1rem",
  fontSize: "0.875rem",
  color: "#374151"
};

const actionButtonStyle = {
  padding: "0.5rem 1rem",
  border: "none",
  borderRadius: "0.5rem",
  fontSize: "0.875rem",
  fontWeight: "500",
  cursor: "pointer",
  transition: "opacity 0.2s"
};
